import { ElementType } from 'react';
import { css } from '@emotion/react';
import { Colors } from '../common/Colors';
import { helperLoadingText, helperTextEllipsis } from '.';
import { Media } from '../types';
import { getMediaProp, setDeclaration } from '../utils';
import { Variables } from '../common/Variables';

type ViewType =
  | 'positive'
  | 'negative'
  | 'rejected'
  | 'success'
  | 'disabled'
  | 'hint'
  | 'normal'
  | false;

export type SizeType =
  | 'normal'
  | 'small'
  | 'extra-small'
  | 'XXS'
  | 'XS'
  | 'XS-Compact-Height'
  | 'S'
  | 'S-Compact-Height'
  | 'M'
  | 'M-Compact-Height'
  | 'L'
  | 'XL'
  | 'XXL'
  | 'XXXL';

type LhType =
  | 'small'
  | 'normal'
  | 'big'
  | 'XXS'
  | 'XS'
  | 'XS-Compact-Height'
  | 'S'
  | 'S-Compact-Height'
  | 'M'
  | 'M-Compact-Height'
  | 'L'
  | 'XL'
  | 'XXL'
  | 'XXXL';

type WeightType = 'normal' | 'bold';

export interface TypographyTextProps {
  as?: ElementType;
  view?: ViewType;
  weight?: WeightType | Media<WeightType>;
  caps?: boolean;
  capitalize?: boolean;
  /** Если не указан size, то font-size и line-height наследуется от родителя.  */
  size?: SizeType | Media<SizeType>;
  customColor?: string | false;
  nowrap?: boolean;
  ellipsis?: boolean;
  id?: string;
  title?: any;
  lh?: LhType;
  loading?: boolean;
}

export const fontSizeList: { [key in SizeType]: string } = {
  // легаси размеры для поддержи старого кода UTEX (не использовать)
  'extra-small': '1.2rem',
  small: '1.3rem',
  normal: '1.4rem',

  // по дефолту размеры font-size для UTEX
  XXS: Variables.typoTextFsXXS,
  XS: Variables.typoTextFsXS,
  'XS-Compact-Height': Variables.typoTextFsXSCH,
  S: Variables.typoTextFsS,
  'S-Compact-Height': Variables.typoTextFsSCH,
  M: Variables.typoTextFsM,
  'M-Compact-Height': Variables.typoTextFsMCH,
  L: Variables.typoTextFsL,
  XL: Variables.typoTextFsXL,
  XXL: Variables.typoTextFsXXL,
  XXXL: Variables.typoTextFsXXXL
};

export const lineHeightList: { [key in LhType]: string | number } = {
  // легаси размеры для UTEX для поддержки старого кода (не использовать)
  small: 1.2,
  normal: 1.334,
  big: 1.54,

  // по дефолту размеры font-size для UTEX
  XXS: Variables.typoTextLhXXS,
  XS: Variables.typoTextLhXS,
  'XS-Compact-Height': Variables.typoTextLhXSCH,
  S: Variables.typoTextLhS,
  'S-Compact-Height': Variables.typoTextLhSCH,
  M: Variables.typoTextLhM,
  'M-Compact-Height': Variables.typoTextLhMCH,
  L: Variables.typoTextLhL,
  XL: Variables.typoTextLhXL,
  XXL: Variables.typoTextLhXXL,
  XXXL: Variables.typoTextLhXXXL
};

const getFontSize = (size?: SizeType | Media<SizeType>) => {
  return getMediaProp(size, fontSizeList);
};

const getLineHeight = (size?: SizeType | Media<SizeType>, lh?: LhType) => {
  if (lh) {
    return lineHeightList[lh];
  }

  return getMediaProp(size, lineHeightList);
};

const getColor = (view?: ViewType) => {
  switch (view) {
    case 'disabled':
      return Colors.text.disabled;

    case 'hint':
      return Colors.text.defaultDim;

    case 'negative':
      return Colors.text.alert;

    case 'positive':
      return Colors.text.primary;

    case 'rejected':
      return Colors.text.error;

    case 'success':
      return Colors.text.success;

    case 'normal':
      return Colors.text.default;

    default:
      return null; // Если задать цвет по умолчанию для Text, то он перестанет наследовать родитеский цвет, а это необходиомо в некоторых случаях. Например в history, когда всей строке (tr) таблицы задается серый цвет, чтобы показать, что она задизейблена
  }
};

export const helperTypographyText = (props: TypographyTextProps = {}) => {
  const {
    view,
    weight,
    caps,
    capitalize,
    size, // никаких значений по умолчанию для size, иначе размер не будет наследоваться от родителя
    customColor,
    nowrap,
    ellipsis,
    lh,
    loading
  } = props;

  return css`
    ${setDeclaration('fontSize', getFontSize(size))};
    ${setDeclaration('lineHeight', getLineHeight(size, lh))};
    ${setDeclaration('fontWeight', weight)};
    text-transform: ${(caps && 'uppercase') || (capitalize && 'capitalize')};
    letter-spacing: ${caps ? (weight === 'bold' ? '0.115em' : '0.076em') : 'normal'};
    color: ${customColor || getColor(view)};
    word-wrap: break-word;
    white-space: ${nowrap && !ellipsis && 'nowrap'};
    ${ellipsis && helperTextEllipsis};
    ${loading && helperLoadingText()};
  `;
};
